import { Button, Column, Grid, InlineNotification, SkipToContent } from "@carbon/react";
import {
  ChartLine,
  CheckmarkOutline,
  DocumentExport,
  Play,
  Restart,
  SettingsAdjust,
  TemperatureHot,
  DataTable,
} from "@carbon/react/icons";
import { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  ExportReceipt,
  ResultSwitcher,
  ScientificAppShell,
  ScientificAutosaveStatus,
  ScientificEmptyState,
  ScientificHeader,
  ScientificModelScope,
  ScientificOutcomeSummary,
  ScientificPreflightSummary,
  ScientificRecoveryNotice,
  ScientificReproducibilityManifest,
  ScientificResultsLayout,
  ScientificResultsToolbar,
  ScientificRunControl,
  ScientificStatusBar,
  ScientificStageHeader,
  ScientificToolRail,
  ScientificValidationSummary,
  useScientificAutosave,
  useScientificFormValidity,
  useScientificResultTransition,
  type ScientificActionDescriptor,
  type ScientificCheckDescriptor,
  type ScientificStatusDescriptor,
  type WorkflowItem,
} from "@jorpago2/scientific-ui";
import { ConfigurationPanel } from "./components/ConfigurationPanel";
import { ExperimentOverview } from "./components/ExperimentOverview";
import { Studies, type ConvergenceEvidence } from "./components/Studies";
import { downloadJsonFile } from "./download";
import { VO2_REFERENCE_CONFIG } from "./solver/defaults";
import { cancelActiveSimulation, runSimulation } from "./solver/workerClient";
import type { OptothermalConfig, OptothermalResult } from "./solver/types";
import { getMeshDiagnostics, isSavedOptothermalConfig, restoreMeshConfig, validateConfig, validateResult, type SavedOptothermalConfig } from "./solver/validation";

type AppView = "configure" | "results" | "validation" | "studies";
type MapView = "peak" | "final";
type RunLifecycle = "idle" | "running" | "completed" | "failed" | "cancelled";

const loadPlots = () => import("./components/Plots");
const TemperatureTransientPlot = lazy(() => loadPlots().then((module) => ({ default: module.TemperatureTransientPlot })));
const PhaseTransientPlot = lazy(() => loadPlots().then((module) => ({ default: module.PhaseTransientPlot })));
const RadialTemperaturePlot = lazy(() => loadPlots().then((module) => ({ default: module.RadialTemperaturePlot })));
const TemperatureMapPlot = lazy(() => loadPlots().then((module) => ({ default: module.TemperatureMapPlot })));
const DepthProfiles = lazy(() => import("./components/DepthProfiles").then((module) => ({ default: module.DepthProfiles })));

const workflow: WorkflowItem[] = [
  { id: "configure", label: "Configure", controlsId: "configuration-panel", icon: <SettingsAdjust size={20} /> },
  { id: "results", label: "Results", controlsId: "results-view", icon: <ChartLine size={20} /> },
  { id: "validation", label: "Validation", controlsId: "validation-view", icon: <CheckmarkOutline size={20} /> },
  { id: "studies", label: "Studies", controlsId: "studies-view", icon: <DataTable size={20} /> },
];

function cloneReferenceConfig(): OptothermalConfig {
  return { ...VO2_REFERENCE_CONFIG };
}

function downloadJson(config: OptothermalConfig, result: OptothermalResult, quantitativeWarnings: readonly { id: string; message: string }[], convergence?: ConvergenceEvidence) {
  downloadJsonFile({
    schema: "optothermal-simulator/result@3",
    generatedAt: new Date().toISOString(),
    model: "axisymmetric-rz-local-tmm-thermal@0.3",
    config,
    result,
    convergence,
    interpretation: {
      quantitativeUse: quantitativeWarnings.length ? "provisional" : "no-resolution-warnings",
      quantitativeWarnings,
    },
  }, "optothermal-vo2-result.json");
}

export function App() {
  const [activeView, setActiveView] = useState<AppView>("configure");
  const [configurationOpen, setConfigurationOpen] = useState(true);
  const [config, setConfig] = useState<OptothermalConfig>(cloneReferenceConfig);
  const [lastRunConfig, setLastRunConfig] = useState<OptothermalConfig>();
  const [result, setResult] = useState<OptothermalResult>();
  const [busy, setBusy] = useState(false);
  const [studyBusy, setStudyBusy] = useState(false);
  const [convergence, setConvergence] = useState<ConvergenceEvidence>();
  const studyStopRef = useRef<(() => void) | null>(null);
  const [runLifecycle, setRunLifecycle] = useState<RunLifecycle>("idle");
  const [error, setError] = useState("");
  const [runtimeMs, setRuntimeMs] = useState<number>();
  const [mapView, setMapView] = useState<MapView>("peak");
  const [exported, setExported] = useState(false);
  const outcomeRef = useRef<HTMLElement>(null);
  const stageRef = useRef<HTMLDivElement>(null);
  const configureTriggerRef = useRef<HTMLButtonElement>(null);
  const runRequestRef = useRef(0);
  const {
    hasInvalidFields,
    isValid: draftsAreValid,
    reportFieldValidity,
    resetValidity,
    revision: fieldRevision,
  } = useScientificFormValidity();
  const restoreConfiguration = useCallback((saved: SavedOptothermalConfig) => {
    runRequestRef.current += 1;
    cancelActiveSimulation();
    resetValidity();
    setConfig(restoreMeshConfig(saved));
    setLastRunConfig(undefined);
    setResult(undefined);
    setBusy(false);
    setRunLifecycle("idle");
    setError("");
    setRuntimeMs(undefined);
    setExported(false);
    setActiveView("configure");
    setConfigurationOpen(true);
  }, [resetValidity]);
  const autosave = useScientificAutosave<SavedOptothermalConfig>({
    storageKey: "optothermal-simulator:session",
    value: config,
    schemaVersion: 1,
    validate: isSavedOptothermalConfig,
    onRestore: restoreConfiguration,
  });
  const issues = useMemo(() => validateConfig(config), [config]);
  const hasErrors = issues.some((issue) => issue.severity === "error");
  const runBlocked = hasErrors || hasInvalidFields;
  const modified = Boolean(result && lastRunConfig && JSON.stringify(config) !== JSON.stringify(lastRunConfig));
  const lastRunIssues = useMemo(() => lastRunConfig ? validateConfig(lastRunConfig) : [], [lastRunConfig]);
  const currentConvergence = convergence && lastRunConfig && JSON.stringify(convergence.config) === JSON.stringify(lastRunConfig) ? convergence : undefined;
  const quantitativeWarnings = useMemo(
    () => [...lastRunIssues.filter((issue) => issue.severity === "warning"), currentConvergence
      ? { id: "refinement-scope", message: `A refinement comparison is available in Studies (${currentConvergence.complete ? "completed" : "partial"}; ${currentConvergence.comparisons.length} directions checked). Pairwise agreement does not establish universal convergence or validate the material model.` }
      : { id: "refinement-pending", message: "A mesh/time refinement comparison has not been run. Resolution checks alone do not establish quantitative convergence." }],
    [lastRunIssues, currentConvergence],
  );
  const resultIsStale = Boolean(result && (modified || busy || runLifecycle === "cancelled" || runLifecycle === "failed"));

  const status: ScientificStatusDescriptor = busy
    ? { state: "running", label: "Simulation running", detail: "Rust/WASM axisymmetric solve" }
    : runLifecycle === "cancelled"
      ? { state: "modified", label: "Run cancelled", detail: "The previous result is retained; run again to refresh it." }
      : error
      ? { state: "failed", label: "Simulation failed", detail: error }
        : modified
          ? { state: "modified", label: "Inputs modified", detail: "Run again to update the results." }
        : result
          ? quantitativeWarnings.length
            ? { state: "warning", label: "Results current · provisional", detail: "Fixed axial position; review resolution warnings before quantitative use." }
            : { state: "up-to-date", label: "Results up to date", detail: "Fixed axial position" }
          : runBlocked
            ? { state: "needs-input", label: "Review inputs" }
            : { state: "ready", label: "Ready", detail: "VO₂ reference preset" };
  const resultStatus: ScientificStatusDescriptor = busy
    ? { state: "running", label: "Refreshing result", detail: "The previous result remains visible until this run completes." }
    : runLifecycle === "cancelled"
      ? { state: "modified", label: "Previous result retained", detail: "The last run was cancelled; values below are not a new result." }
      : runLifecycle === "failed"
        ? { state: "failed", label: "Previous result retained", detail: "The latest run failed; values below are from the last successful run." }
        : modified
          ? { state: "modified", label: "Result is stale", detail: "Inputs changed after this result; run again before interpreting or exporting it." }
          : { state: "up-to-date", label: "Current result", detail: "Result matches the last completed configuration run." };

  const run = useCallback(async () => {
    if (studyBusy) return;
    if (!draftsAreValid() || validateConfig(config).some((issue) => issue.severity === "error")) {
      setActiveView("configure");
      return;
    }
    const requestId = ++runRequestRef.current;
    setBusy(true);
    setRunLifecycle("running");
    setError("");
    setExported(false);
    setActiveView("results");
    const started = performance.now();
    try {
      const [next] = await Promise.all([runSimulation(config), loadPlots()]);
      if (runRequestRef.current !== requestId) return;
      setResult(next);
      setLastRunConfig({ ...config });
      setRuntimeMs(performance.now() - started);
      setRunLifecycle("completed");
    } catch (cause) {
      if (runRequestRef.current !== requestId) return;
      const cancelled = cause instanceof DOMException && cause.name === "AbortError";
      setRunLifecycle(cancelled ? "cancelled" : "failed");
      setError(cancelled ? "Simulation cancelled." : cause instanceof Error ? cause.message : String(cause));
    } finally {
      if (runRequestRef.current === requestId) setBusy(false);
    }
  }, [config, draftsAreValid, studyBusy]);

  useEffect(() => {
    stageRef.current?.scrollIntoView({ block: "start", behavior: "auto" });
  }, [activeView]);

  useEffect(() => {
    if (activeView === "configure" && !configurationOpen) configureTriggerRef.current?.focus();
  }, [activeView, configurationOpen]);

  useEffect(() => () => {
    runRequestRef.current += 1;
    cancelActiveSimulation();
  }, []);

  useScientificResultTransition({
    state: status.state,
    resultRef: outcomeRef,
    completionKey: runtimeMs,
    onReveal: () => setActiveView("results"),
  });

  const updateConfig = (field: keyof OptothermalConfig, value: number) => {
    setConfig((current) => ({ ...current, [field]: value }));
    setRunLifecycle("idle");
    setError("");
  };

  const resetPreset = () => {
    resetValidity();
    setConfig(cloneReferenceConfig());
    setRunLifecycle("idle");
    setError("");
  };

  const stop = () => {
    runRequestRef.current += 1;
    cancelActiveSimulation();
    setBusy(false);
    setRunLifecycle("cancelled");
    setError("Simulation cancelled.");
  };

  const resultActions = useMemo<ScientificActionDescriptor[]>(() => result && lastRunConfig ? [
    {
      id: "export-result",
      label: "Export result",
      shortLabel: "Export",
      icon: DocumentExport,
      emphasis: "secondary",
      onClick: () => { downloadJson(lastRunConfig, result, quantitativeWarnings, currentConvergence); setExported(true); },
      disabled: busy || resultIsStale,
      disabledReason: resultIsStale ? "Run the current configuration before exporting." : undefined,
    },
    {
      id: "rerun-model",
      label: "Run again",
      icon: Restart,
      emphasis: "ghost",
      onClick: () => { void run(); },
      disabled: busy || studyBusy || runBlocked,
    },
    { id: "compare-case", label: "Compare and save", icon: DataTable, emphasis: "ghost", onClick: () => { setActiveView("studies"); setConfigurationOpen(false); } },
  ] : [], [busy, studyBusy, lastRunConfig, quantitativeWarnings, currentConvergence, result, resultIsStale, run, runBlocked]);

  const preflightChecks = useMemo<ScientificCheckDescriptor[]>(() => {
    const pointsPerFwhm = (config.timeSteps - 1) * config.pulseFwhmNs / config.durationNs;
    const meshCells = config.radialCells * (config.substrateCells + config.filmCells);
    const mesh = getMeshDiagnostics(config);
    return [
      { id: "input", label: "Parameter ranges", state: runBlocked ? "failed" : "passed", detail: hasInvalidFields ? "A visible field contains an uncommitted invalid value." : hasErrors ? "Correct the blocking input messages." : "All required values are finite and within solver limits." },
      { id: "time", label: "Pulse resolution", state: pointsPerFwhm >= 16 ? "passed" : "warning", value: `${pointsPerFwhm.toFixed(1)} points/FWHM`, detail: "Implicit integration removes a stability restriction but not temporal discretization error." },
      { id: "radius", label: "Radial boundary", state: config.radiusUm >= 4 * config.waistUm ? "passed" : "warning", value: `${(config.radiusUm / config.waistUm).toFixed(1)} w₀`, detail: "A boundary at four beam waists limits interaction with the heated region." },
      { id: "source-mesh", label: "Gaussian source resolution", state: mesh.pointsPerWaist < 2 ? "failed" : mesh.pointsPerWaist < 8 ? "warning" : "passed", value: `${mesh.pointsPerWaist.toFixed(2)} cells/w₀`, detail: `Radial spacing ${mesh.radialSpacingUm.toPrecision(3)} µm; at least 8 intervals per waist are recommended for quantitative work.` },
      { id: "depth-mesh", label: "Substrate diffusion resolution", state: !Number.isFinite(mesh.cellsPerSubstrateDiffusionLength) || mesh.cellsPerSubstrateDiffusionLength < 0.01 ? "failed" : mesh.cellsPerSubstrateDiffusionLength < 4 ? "warning" : "passed", value: Number.isFinite(mesh.cellsPerSubstrateDiffusionLength) ? `${mesh.cellsPerSubstrateDiffusionLength.toPrecision(3)} cells/Ld` : "Invalid mesh", detail: Number.isFinite(mesh.cellsPerSubstrateDiffusionLength) ? `Largest cell intersecting the pulse diffusion layer: ${(mesh.substrateDiffusionSpacingUm * 1000).toPrecision(3)} nm; diffusion length ${(mesh.substrateDiffusionLengthUm * 1000).toPrecision(3)} nm. Grading ${config.substrateGrading} (0 is uniform).` : "Correct substrate cell count and grading." },
      { id: "film-mesh", label: "Film depth resolution", state: !Number.isInteger(config.filmCells) || config.filmCells < 1 || config.filmCells > 64 ? "failed" : mesh.cellsPerFilmDiffusionLength < 4 ? "warning" : "passed", value: `${config.filmCells} cells · ${mesh.cellsPerFilmDiffusionLength.toPrecision(3)} cells/Ld`, detail: `Cell thickness ${mesh.filmSpacingNm.toPrecision(3)} nm; pulse diffusion length ${mesh.filmDiffusionLengthNm.toPrecision(3)} nm. Thermal depth is resolved; the optical phase remains an effective film value.` },
      { id: "mesh", label: "Browser mesh", state: meshCells <= 40_000 ? "passed" : "failed", value: `${meshCells.toLocaleString()} cells`, detail: "The hard limit bounds worker memory and interaction latency." },
    ];
  }, [config, hasErrors, hasInvalidFields, runBlocked]);

  const validationChecks = useMemo<ScientificCheckDescriptor[]>(() => {
    if (!result) return [
      { id: "not-run", label: "Simulation result", state: "not-run", detail: "Run the model before evaluating numerical checks." },
    ];
    const checks = validateResult(result);
    return [
      { id: "schema", label: "Result schema", state: checks.schema ? "passed" : "failed", detail: "All arrays must match the requested r–z mesh and time axis." },
      { id: "finite", label: "Finite solution", state: checks.finite ? "passed" : "failed", detail: "All temporal and spatial temperature samples must remain finite." },
      { id: "ranges", label: "Physical ranges", state: checks.physicalRanges ? "passed" : "failed", detail: "Temperature, phase fraction and absorptance must remain in their supported ranges." },
      { id: "linear", label: "Linear convergence", state: checks.converged ? "passed" : "failed", value: `r = ${result.metrics.worstLinearResidual.toExponential(2)}`, detail: `Worst step ${result.metrics.worstLinearStep}; ${result.metrics.maximumLinearIterations} maximum iterations; tolerance ${result.metrics.linearResidualTolerance.toExponential(1)}.` },
      { id: "passive", label: "Optical passivity", state: checks.passive ? "passed" : "failed", value: `Araw = ${result.metrics.minimumAbsorptanceRaw.toFixed(4)}–${result.metrics.maximumAbsorptanceRaw.toFixed(4)}`, detail: `Raw baseline balance: R ${result.metrics.baselineReflectance.toFixed(5)} + T ${result.metrics.baselineTransmittance.toFixed(5)} + A ${result.metrics.baselineAbsorptanceRaw.toFixed(5)}.` },
      { id: "energy", label: "Thermal energy bound", state: checks.energyBound ? "passed" : "failed", value: `${(100 * result.metrics.storedToAbsorbedRatio).toFixed(2)}% stored/absorbed`, detail: "Stored sensible heat cannot exceed integrated absorbed optical energy." },
      { id: "convergence", label: "Mesh convergence", state: "warning", detail: currentConvergence ? `${currentConvergence.comparisons.length} independent refinement comparisons available in Studies; ${currentConvergence.comparisons.filter((entry) => entry.comparison.status === "outside-tolerance").length} exceed the selected tolerance. ${currentConvergence.skipped.length} skipped. Quantitative use remains provisional.` : "A refinement comparison has not been run. Treat quantitative values as provisional." },
    ];
  }, [result, currentConvergence]);

  const validationPassed = result ? validateResult(result) : undefined;
  const validationStatus: ScientificStatusDescriptor = !result
    ? { state: "needs-input", label: "Not evaluated" }
    : validationPassed && Object.values(validationPassed).every(Boolean)
      ? { state: "warning", label: currentConvergence ? "Core checks passed; refinement available" : "Core checks passed; convergence pending" }
      : { state: "failed", label: "Validation check failed" };

  const panelOpen = activeView === "configure" && configurationOpen;
  const navigationItems = workflow.map((item) => item.id === "results" && busy ? { ...item, status: "loading" as const, statusLabel: "Simulation running" } : item.id === "studies" && studyBusy ? { ...item, status: "loading" as const, statusLabel: "Study running" } : item);

  return (
    <ScientificAppShell
      className="optothermal-app"
      panelOpen={panelOpen}
      recovery={autosave.recovery && (
        <ScientificRecoveryNotice
          savedAt={autosave.recovery.savedAt}
          onRestore={autosave.restore}
          onDiscard={autosave.discard}
          title="Previous optothermal session available"
          description="A validated local configuration was saved during an earlier visit. Restore it to continue, or discard it and use the VO₂ reference preset."
        />
      )}
      header={(
        <ScientificHeader
          skipLink={<SkipToContent href="#optothermal-workspace">Skip to simulation workspace</SkipToContent>}
          product="Optothermal Simulator"
          compactProduct="Optothermal"
          descriptor="Axisymmetric VO₂"
          productMark={<TemperatureHot size={24} aria-hidden="true" />}
          contextLabel="FIXED POSITION"
          context={`z = 0 · λ ${config.wavelengthUm} µm`}
          status={studyBusy ? { state: "running", label: "Study running" } : status}
          primaryAction={<ScientificRunControl execution={{ ...(studyBusy ? { state: "running" as const, label: "Study running" } : status), onRun: () => { void run(); }, onStop: studyBusy ? () => { studyStopRef.current?.(); } : stop, runLabel: "Run", stopLabel: "Stop", disabled: runBlocked && !studyBusy, disabledReason: runBlocked ? "Review invalid inputs before running." : undefined }} size="sm" />}
          help={{
            summary: "Configure a single axial position, run the Rust/WASM r–z solver, then inspect temperature, phase state and validation evidence.",
            footer: "The reference material values are not a substitute for sample-specific calibration.",
          }}
        />
      )}
      navigation={(
        <ScientificToolRail
          items={navigationItems}
          activeId={activeView}
          expandedId={panelOpen ? "configure" : null}
          collapsible
          registerItemRef={(id, node) => {
            if (id === "configure") configureTriggerRef.current = node;
          }}
          onChange={(id) => {
            if (id === null && activeView === "configure") {
              setConfigurationOpen(false);
              return;
            }
            const nextView = (id ?? (result ? "results" : "configure")) as AppView;
            setActiveView(nextView);
            setConfigurationOpen(nextView === "configure");
          }}
        />
      )}
      panel={(
        <div id="configuration-panel">
          <ConfigurationPanel
            config={config}
            issues={issues}
            busy={busy || studyBusy}
            onChange={updateConfig}
            onReset={resetPreset}
            onClose={() => setConfigurationOpen(false)}
            onFieldValidationChange={reportFieldValidity}
            fieldRevision={fieldRevision}
            hasInvalidDrafts={hasInvalidFields}
          />
        </div>
      )}
      statusBar={<ScientificStatusBar status={studyBusy ? { state: "running", label: "Study running" } : status} metadata={<><ScientificAutosaveStatus status={autosave.status} savedAt={autosave.lastSavedAt} /><span>{`${config.radialCells} × ${config.substrateCells + config.filmCells} r–z cells · ${config.timeSteps} time samples · ${result ? result.engine : "Rust/WASM"}`}</span></>} />}
    >
      <div ref={stageRef} id="optothermal-workspace" className="optothermal-stage" tabIndex={-1}>
        <h1 className="optothermal-visually-hidden">Optothermal Simulator</h1>
        <section id="results-view" aria-label="Simulation results" hidden={activeView !== "results"}>
            {error && <InlineNotification kind="error" title="Simulation failed" subtitle={error} lowContrast hideCloseButton />}
            {!result ? (
              <ScientificEmptyState
                title={busy ? "Solving the optothermal response" : "No result yet"}
                description={busy ? "The axisymmetric thermal solve is running in a background worker." : "Review the reference preset and run the fixed-position model."}
                icon={<TemperatureHot size={32} />}
                action={!busy ? <Button type="button" renderIcon={Play} onClick={() => { setActiveView("configure"); setConfigurationOpen(true); }}>Open configuration</Button> : undefined}
              />
            ) : (
              <ScientificResultsLayout
                className="optothermal-results"
                title="Fixed-position response"
                description="One Gaussian-beam position at z = 0; no axial sweep or detector propagation."
                status={resultStatus}
                actions={<ScientificResultsToolbar actions={resultActions} />}
              >
                {exported && (
                  <ExportReceipt
                    className="optothermal-export-receipt"
                    fileName="optothermal-vo2-result.json"
                    format="JSON"
                    destination={`Browser downloads${quantitativeWarnings.length ? ` · ${quantitativeWarnings.length} quantitative warning${quantitativeWarnings.length === 1 ? "" : "s"} included` : ""}`}
                    onDismiss={() => setExported(false)}
                  />
                )}
                {quantitativeWarnings.length > 0 && <InlineNotification
                  className="optothermal-quantitative-warning"
                  kind="warning"
                  title="Quantitative result is provisional"
                  subtitle={`${quantitativeWarnings.map((issue) => issue.message).join(" ")} These warnings are retained in the exported JSON.`}
                  lowContrast
                  hideCloseButton
                />}
                <section ref={outcomeRef} tabIndex={-1}>
                  <ScientificOutcomeSummary
                    title="Optothermal pulse completed"
                    headingLevel={3}
                    status={resultStatus}
                    summary={result.metrics.maximumMetallicFraction > 0.5
                      ? "The beam centre enters the metallic branch during this pulse."
                      : "The beam centre remains predominantly insulating during this pulse."}
                    metrics={[
                      { id: "temperature", label: "Peak center temperature", value: result.metrics.maximumTemperatureC, unit: "°C", format: { significantDigits: 5 } },
                      { id: "phase", label: "Maximum metallic fraction", value: result.metrics.maximumMetallicFraction, format: { significantDigits: 4 } },
                      { id: "absorption", label: "Peak absorptance", value: result.metrics.peakAbsorptance, format: { significantDigits: 4 } },
                      { id: "runtime", label: "Browser runtime", value: runtimeMs ? runtimeMs / 1000 : 0, unit: "s", format: { significantDigits: 3 } },
                    ]}
                  />
                </section>
                <Suspense fallback={<p className="plot-loading" role="status">Loading scientific plots…</p>}>
                  <Grid fullWidth narrow className="plot-grid">
                    <Column sm={4} md={8} lg={8} className="plot-column">
                      <TemperatureTransientPlot result={result} />
                      <RadialTemperaturePlot result={result} />
                    </Column>
                    <Column sm={4} md={8} lg={8} className="plot-column">
                      <PhaseTransientPlot result={result} />
                      <div className="map-result">
                        <ResultSwitcher options={[{ id: "peak", label: "Peak map" }, { id: "final", label: "Final map" }]} activeId={mapView} onChange={(id) => setMapView(id as MapView)} label="Temperature map" />
                        <TemperatureMapPlot result={result} view={mapView} />
                      </div>
                    </Column>
                  </Grid>
                  {lastRunConfig && <DepthProfiles result={result} config={lastRunConfig} />}
                </Suspense>
                <section className="optothermal-coupling" aria-labelledby="optothermal-coupling-title">
                  <h4 id="optothermal-coupling-title">Optical → thermal coupling</h4>
                  <dl>
                    <div><dt>Baseline reflectance R₀</dt><dd>{result.metrics.baselineReflectance.toPrecision(5)}</dd></div>
                    <div><dt>Baseline transmittance T₀</dt><dd>{result.metrics.baselineTransmittance.toPrecision(5)}</dd></div>
                    <div><dt>Baseline absorptance A₀</dt><dd>{result.metrics.baselineAbsorptance.toPrecision(5)}</dd></div>
                    <div><dt>Stored / absorbed energy</dt><dd>{(100 * result.metrics.storedToAbsorbedRatio).toFixed(2)}%</dd></div>
                  </dl>
                  <p>R/T/A are the thin-film optical balance at the reference state; the thermal solver uses the local absorptance as its heat source. They are not detector-plane observables.</p>
                </section>
              </ScientificResultsLayout>
            )}
        </section>
        <section id="studies-view" aria-labelledby="studies-title" hidden={activeView !== "studies"}>
          <Studies config={config} result={result} lastRunConfig={lastRunConfig} blocked={runBlocked} simulationBusy={busy} resultStale={resultIsStale} stopRef={studyStopRef}
            onBusyChange={setStudyBusy} onConvergence={setConvergence} onLoadConfiguration={restoreConfiguration} />
        </section>
        <section id="validation-view" aria-labelledby="validation-title" hidden={activeView !== "validation"}>
            <ScientificStageHeader
              title="Model and validation"
              titleId="validation-title"
              description="Separate numerical sanity checks from the convergence and sample calibration still required for quantitative use."
            />
            <Grid fullWidth narrow className="validation-grid">
              <Column sm={4} md={8} lg={8}>
                <ScientificPreflightSummary status={runBlocked ? { state: "failed", label: "Inputs blocked" } : issues.length ? { state: "warning", label: "Ready with warnings" } : { state: "ready", label: "Ready" }} checks={preflightChecks} />
              </Column>
              <Column sm={4} md={8} lg={8}>
                <ScientificValidationSummary status={validationStatus} checks={validationChecks} />
              </Column>
              <Column sm={4} md={8} lg={16}>
                <ScientificModelScope
                  model="Normal-incidence coherent thin-film TMM supplies local absorptance to an implicit axisymmetric r–z finite-volume heat solver. A hysteretic, relaxing metallic fraction interpolates the VO₂ optical constants."
                  assumptions={[
                    "Gaussian beam evaluated at a single fixed axial position.",
                    "Isotropic, temperature-independent thermal properties.",
                    "One optical film layer and a finite glass substrate with ambient outer boundaries.",
                    "Uniform absorbed power through the film; effective phase driven by thickness-averaged temperature.",
                  ]}
                  limits={[
                    "No Z-scan propagation or detector-plane observable is calculated.",
                    "No latent heat, temperature-dependent conductivity, stress or ablation.",
                    "Reference optical constants are not sample-specific ellipsometry.",
                    "Quantitative use requires temporal, radial and depth convergence studies.",
                  ]}
                  reference="Defaults migrated from paper_zscan/simulations/configs/materials/vo2_1064_reference.json."
                />
              </Column>
              {result && lastRunConfig && (
                <Column sm={4} md={8} lg={16}>
                  <ScientificReproducibilityManifest
                    status={modified ? { state: "modified", label: "Inputs changed after run" } : { state: "up-to-date", label: "Manifest current" }}
                    items={[
                      { id: "engine", label: "Engine", value: result.engine },
                      { id: "model", label: "Model", value: "axisymmetric-rz-local-tmm-thermal@0.3" },
                      { id: "mesh", label: "Mesh", value: `${lastRunConfig.radialCells} × ${lastRunConfig.substrateCells + lastRunConfig.filmCells} cells; ${lastRunConfig.timeSteps} time samples` },
                      { id: "depth-grid", label: "Depth mesh", value: `${lastRunConfig.filmCells} film cells; ${lastRunConfig.substrateCells} substrate cells; exponential grading ${lastRunConfig.substrateGrading}` },
                      { id: "timestep", label: "Time step", value: `${result.metrics.timeStepNs.toPrecision(5)} ns` },
                      { id: "iterations", label: "Mean implicit iterations", value: result.metrics.averageLinearIterations.toFixed(2) },
                      { id: "worst-residual", label: "Worst linear residual", value: `${result.metrics.worstLinearResidual.toExponential(4)} at step ${result.metrics.worstLinearStep}` },
                      { id: "raw-balance", label: "Baseline R/T/Araw", value: `${result.metrics.baselineReflectance.toPrecision(5)} / ${result.metrics.baselineTransmittance.toPrecision(5)} / ${result.metrics.baselineAbsorptanceRaw.toPrecision(5)}` },
                    ]}
                  />
                </Column>
              )}
            </Grid>
        </section>
        <section id="configure-view" className="configuration-overview" aria-labelledby="configuration-overview-title" hidden={activeView !== "configure"}>
            <ScientificStageHeader
              title="Run overview"
              titleId="configuration-overview-title"
            />
            <Grid fullWidth narrow className="configuration-overview-grid">
              <Column sm={4} md={8} lg={16}>
                <ExperimentOverview config={config} />
              </Column>
              <Column sm={4} md={8} lg={16}>
                <ScientificPreflightSummary
                  compact
                  title="Numerical checks"
                  status={runBlocked ? { state: "failed", label: "Inputs blocked" } : issues.length ? { state: "warning", label: "Ready with warnings" } : { state: "ready", label: "Ready" }}
                  checks={preflightChecks.map((check) => ({
                    ...check,
                    detail: undefined,
                    value: check.value ?? (check.state === "passed" ? "Within limits" : undefined),
                  }))}
                />
              </Column>
            </Grid>
        </section>
      </div>
    </ScientificAppShell>
  );
}
